/**
 * @file pages/TasksPage.js
 * @description Customer Butler — follow-up tasks. "What's on my plate": open
 * tasks sorted by due date (overdue flagged red), add a task, mark done.
 */
import React, { useState, useEffect, useCallback, useContext } from 'react';
import { Navigate } from 'react-router-dom';
import AuthContext from '../contexts/AuthContext';
import { useTheme } from '../hooks/useTheme';
import { Modal } from '../design-system';
import { apiGet, apiSend } from '../lib/api';

function dueLabel(dueAt) {
  if (!dueAt) return { text: 'No due date', color: 'var(--text-tertiary,#9CA3AF)' };
  const d = new Date(dueAt);
  const days = Math.round((d.setHours(0, 0, 0, 0) - new Date().setHours(0, 0, 0, 0)) / 86400000);
  if (days < 0) return { text: `${-days}d overdue`, color: '#DC2626' };
  if (days === 0) return { text: 'Due today', color: '#B45309' };
  return { text: `in ${days}d`, color: 'var(--text-secondary,#6B7280)' };
}
const PRIORITY = { high: '#DC2626', normal: '#6B7280', low: '#9CA3AF' };

function TasksPage() {
  const auth = useContext(AuthContext);
  const { theme } = useTheme();
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDone, setShowDone] = useState(false);
  const [showAdd, setShowAdd] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try { setTasks(await apiGet(`/customer/tasks?status=${showDone ? 'done' : 'open'}`)); }
    catch (e) { setTasks([]); }
    finally { setLoading(false); }
  }, [showDone]);
  useEffect(() => { load(); }, [load]);

  if (!auth.isAuthenticated) return <Navigate to="/login" />;

  async function complete(id) { await apiSend(`/customer/tasks/${id}/complete`, 'POST', {}); load(); }

  return (
    <div className="page-content" style={{ padding: '1rem', maxWidth: 800, margin: '0 auto' }}>
      <div style={T.header}>
        <h2 style={{ margin: 0 }}>Follow-ups</h2>
        <button style={{ ...T.btnPrimary, backgroundColor: theme.accentColor }} onClick={() => setShowAdd(true)}>+ Add Task</button>
      </div>
      <div style={T.toggle}>
        <button style={{ ...T.tab, ...(!showDone ? T.tabOn : {}) }} onClick={() => setShowDone(false)}>Open</button>
        <button style={{ ...T.tab, ...(showDone ? T.tabOn : {}) }} onClick={() => setShowDone(true)}>Done</button>
      </div>

      {loading ? <div style={T.muted}>Loading…</div>
        : tasks.length === 0 ? <div style={T.empty}><h3>{showDone ? 'No completed tasks' : 'Nothing on your plate 🎉'}</h3><p style={{ color: 'var(--text-secondary,#666)' }}>{showDone ? '' : 'Add a follow-up so nothing slips.'}</p></div>
        : tasks.map((t) => {
          const dl = dueLabel(t.dueAt);
          return (
            <div key={t.id} style={T.row}>
              {!showDone && <input type="checkbox" onChange={() => complete(t.id)} style={{ width: 18, height: 18, cursor: 'pointer' }} title="Mark done" />}
              <div style={{ flex: 1 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                  <span style={{ width: 8, height: 8, borderRadius: 99, background: PRIORITY[t.priority] || PRIORITY.normal, display: 'inline-block' }} />
                  <strong style={{ textDecoration: showDone ? 'line-through' : 'none' }}>{t.title}</strong>
                </div>
                {t.notes && <div style={T.notes}>{t.notes}</div>}
              </div>
              <span style={{ fontSize: '0.78rem', color: dl.color, whiteSpace: 'nowrap' }}>{showDone ? (t.completedAt ? new Date(t.completedAt).toLocaleDateString() : 'done') : dl.text}</span>
            </div>
          );
        })}

      <Modal isOpen={showAdd} onClose={() => setShowAdd(false)} title="Add Follow-up Task">
        <AddTaskForm onCancel={() => setShowAdd(false)} onSaved={() => { setShowAdd(false); setShowDone(false); load(); }} />
      </Modal>
    </div>
  );
}

function AddTaskForm({ onCancel, onSaved }) {
  const [form, setForm] = useState({ title: '', dueAt: '', priority: 'normal', customerId: '', notes: '' });
  const [customers, setCustomers] = useState([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  useEffect(() => { apiGet('/customer/customers?limit=100').then(setCustomers).catch(() => setCustomers([])); }, []);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  async function submit(e) {
    e.preventDefault(); setSaving(true); setError(null);
    const body = { ...form };
    if (!body.dueAt) delete body.dueAt;
    if (!body.customerId) delete body.customerId;
    const r = await apiSend('/customer/tasks', 'POST', body);
    setSaving(false);
    if (r.ok) onSaved(); else setError((r.error && r.error.message) || 'Failed to add task');
  }
  return (
    <form onSubmit={submit} style={{ padding: '1rem' }}>
      {error && <div style={T.error}>{error}</div>}
      <div style={T.field}><label style={T.label}>Task *</label>
        <input style={T.input} value={form.title} onChange={(e) => set('title', e.target.value)} required autoFocus placeholder="Call Dale back about the quote" /></div>
      <div style={T.frow}>
        <div style={T.field}><label style={T.label}>Due</label><input style={T.input} type="date" value={form.dueAt} onChange={(e) => set('dueAt', e.target.value)} /></div>
        <div style={T.field}><label style={T.label}>Priority</label>
          <select style={T.input} value={form.priority} onChange={(e) => set('priority', e.target.value)}>
            <option value="low">Low</option><option value="normal">Normal</option><option value="high">High</option>
          </select></div>
      </div>
      <div style={T.field}><label style={T.label}>Customer (optional)</label>
        <select style={T.input} value={form.customerId} onChange={(e) => set('customerId', e.target.value)}>
          <option value="">— None —</option>
          {customers.map((c) => <option key={c.id} value={c.id}>{c.displayName}</option>)}
        </select></div>
      <div style={T.field}><label style={T.label}>Notes</label><textarea style={{ ...T.input, minHeight: 50, resize: 'vertical' }} value={form.notes} onChange={(e) => set('notes', e.target.value)} /></div>
      <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}>
        <button type="button" style={T.btnSecondary} onClick={onCancel}>Cancel</button>
        <button type="submit" disabled={saving} style={{ ...T.btnPrimary, opacity: saving ? 0.6 : 1 }}>{saving ? 'Saving…' : 'Add Task'}</button>
      </div>
    </form>
  );
}

const T = {
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.75rem', flexWrap: 'wrap', gap: '0.5rem' },
  toggle: { display: 'flex', gap: '0.25rem', marginBottom: '1rem' },
  tab: { padding: '0.35rem 0.9rem', border: '1px solid var(--color-border,#E5E7EB)', background: 'var(--surface-card,#fff)', cursor: 'pointer', fontSize: '0.82rem', borderRadius: 999, color: 'var(--text-secondary,#6B7280)' },
  tabOn: { background: 'var(--color-primary-600,#2563EB)', color: '#fff', borderColor: 'var(--color-primary-600,#2563EB)' },
  row: { display: 'flex', alignItems: 'center', gap: '0.75rem', padding: '0.7rem 0.5rem', borderBottom: '1px solid var(--color-border,#E5E7EB)' },
  notes: { fontSize: '0.8rem', color: 'var(--text-secondary,#6B7280)', marginTop: '0.15rem' },
  muted: { color: 'var(--text-secondary,#666)', fontSize: '0.875rem', padding: '1rem 0' },
  empty: { textAlign: 'center', padding: '2.5rem', border: '2px dashed var(--color-border,#E5E7EB)', borderRadius: 10 },
  frow: { display: 'flex', gap: '0.75rem', flexWrap: 'wrap' },
  field: { flex: 1, minWidth: 130, marginBottom: '0.5rem' },
  label: { display: 'block', fontSize: '0.75rem', fontWeight: 500, marginBottom: '0.25rem', color: 'var(--text-secondary,#6B7280)' },
  input: { width: '100%', padding: '0.5rem', border: '1px solid var(--color-border,#E5E7EB)', borderRadius: 6, fontSize: '0.875rem', backgroundColor: 'var(--surface-card,#fff)', color: 'var(--text-primary,#111)', boxSizing: 'border-box' },
  btnPrimary: { padding: '0.5rem 1rem', border: 'none', borderRadius: 6, cursor: 'pointer', fontSize: '0.85rem', fontWeight: 600, backgroundColor: 'var(--color-primary-600,#2563EB)', color: '#fff' },
  btnSecondary: { padding: '0.5rem 1rem', border: '1px solid var(--color-border,#E5E7EB)', borderRadius: 6, backgroundColor: 'var(--surface-card,#fff)', cursor: 'pointer', fontSize: '0.85rem', fontWeight: 500 },
  error: { backgroundColor: '#FEE2E2', color: '#991B1B', padding: '0.5rem 0.75rem', borderRadius: 6, marginBottom: '1rem', fontSize: '0.85rem' },
};

export default TasksPage;
